import { useEffect, useState } from 'react';

import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Alert, ButterflyLoader, Button, Modal } from '@wf/components';

import AttachmentsTableForm from '../AttachmentsTableForm';

import { ArrayFields } from '../../../types/fieldEnums';
import { type IAttachmentFormValues } from '../types';

import styles from './AttachmentBulkUploadModal.module.scss';

interface IAttachmentBulkUploadModalProps {
  title: string;
  onCancel: () => void;
  onConfirm: (data: IAttachmentFormValues[]) => void;
  isUploading?: boolean;
  invalidFiles?: string[];
  uploadedAttachments: IAttachmentFormValues[];
  mode?: 'default' | 'import';
}

function AttachmentBulkUploadModal({
  title,
  onConfirm,
  onCancel,
  isUploading = false,
  invalidFiles = [],
  uploadedAttachments,
  mode = 'default',
}: IAttachmentBulkUploadModalProps) {
  const [showAlert, setShowAlert] = useState(true);
  const { t } = useTranslation();

  const form = useForm({ defaultValues: { [ArrayFields.attachedDocuments]: uploadedAttachments } });

  const { reset, getValues } = form;

  const [checkedItemIds, setCheckedItemIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (uploadedAttachments.length) {
      reset({ [ArrayFields.attachedDocuments]: uploadedAttachments });
    }
  }, [reset, uploadedAttachments]);

  const handleConfirm = () => {
    const values = getValues(ArrayFields.attachedDocuments);

    onConfirm(mode === 'default' ? values : values.filter(({ id }) => checkedItemIds.has(id as number)));
  };

  return (
    <Modal className={styles.modal} handleClose={onCancel} id="upload-attachments-modal" isOpen>
      <Modal.Header>
        <Modal.Title>{t(title)}</Modal.Title>
      </Modal.Header>
      <Modal.Content className={styles.content}>
        {isUploading ? (
          <>
            <ButterflyLoader className={styles.loader} />
            <span>{t('File Uploading in Progress...')}</span>
          </>
        ) : (
          <>
            {showAlert && invalidFiles.length > 0 && (
              <Alert type="warning" className={styles.alert} onClose={() => setShowAlert(false)}>
                {t(
                  'Upload failed for certain documents due to incorrect formats. The affected documents include: {{fileNames}}.',
                  { fileNames: invalidFiles.join(', ') }
                )}
              </Alert>
            )}
            {uploadedAttachments.length > 0 && (
              <FormProvider {...form}>
                <AttachmentsTableForm
                  setCheckedItemIds={setCheckedItemIds}
                  checkedItemIds={checkedItemIds}
                  deleteCallback={onCancel}
                  mode={mode}
                />
              </FormProvider>
            )}
          </>
        )}
      </Modal.Content>
      {!isUploading && (
        <Modal.Footer>
          <Button onClick={onCancel} color="typography" secondary>
            {t('Cancel')}
          </Button>
          <Button onClick={handleConfirm} disabled={!uploadedAttachments.length}>
            {mode === 'default' ? t('Save') : t('Import')}
          </Button>
        </Modal.Footer>
      )}
    </Modal>
  );
}

export default AttachmentBulkUploadModal;
